import React from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Button, CircularProgress, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';

import { AppPagination } from '@/__core/components';
import EmployeeDisplay from '@/__core/components/EmployeeDisplay';
import DataTableModule from '@/__core/layout/module/DataTableModule';

import { StatusManageItem } from '@/modules/settings/redux/SettingsCRUD';
import { InventoryPurchaseOrderRow } from '../types';

interface PurchaseOrderListProps {
  rows: InventoryPurchaseOrderRow[];
  loading?: boolean;
  statusOptions?: StatusManageItem[];
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onApplyBulkAction?: (action: 'approve' | 'delete', rows: InventoryPurchaseOrderRow[]) => Promise<void> | void;
  bulkActionLoading?: boolean;
  onSelectRow: (row: InventoryPurchaseOrderRow) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export default function PurchaseOrderList({
  rows,
  loading = false,
  statusOptions = [],
  pagination,
  onPageChange,
  onApplyBulkAction,
  bulkActionLoading = false,
  onSelectRow,
  canUpdate = true,
  canDelete = true,
}: PurchaseOrderListProps) {
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [bulkAction, setBulkAction] = React.useState<string>('');
  const canBulkSelect = canUpdate || canDelete;

  React.useEffect(() => {
    const existingIds = new Set(rows.map((row) => row.id));
    setSelectedRowIds((current) => current.filter((id) => existingIds.has(id)));
  }, [rows]);

  const selectedRows = React.useMemo(
    () => rows.filter((row) => selectedRowIds.includes(row.id)),
    [rows, selectedRowIds],
  );

  const handleToggleRow = (id: string) => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedRowIds((current) => (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]));
  };

  const handleToggleSelectAll = () => {
    if (!canBulkSelect) {
      return;
    }
    if (rows.length > 0 && selectedRowIds.length === rows.length) {
      setSelectedRowIds([]);
      return;
    }
    setSelectedRowIds(rows.map((row) => row.id));
  };

  const handleBulkActionChange = (event: SelectChangeEvent<string>) => {
    const nextAction = event.target.value;
    if (nextAction === 'approve' && !canUpdate) {
      return;
    }
    if (nextAction === 'delete' && !canDelete) {
      return;
    }
    setBulkAction(nextAction);
  };

  const handleApplyBulkAction = () => {
    if (!bulkAction) {
      return;
    }

    if (bulkAction === 'delete') {
      if (!canDelete) {
        return;
      }
      onApplyBulkAction?.('delete', selectedRows);
    }
    if (bulkAction === 'approve') {
      if (!canUpdate) {
        return;
      }
      onApplyBulkAction?.('approve', selectedRows);
    }

    setBulkAction('');
    setSelectedRowIds([]);
  };

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryPurchaseOrderRow) => React.ReactNode;
  }> = [
    { id: 'code', label: 'Mã PO', width: '140px', render: (_: unknown, row: InventoryPurchaseOrderRow) => <Typography sx={{ fontWeight: 600 }}>{row.code}</Typography> },
    { id: 'name', label: 'Phiếu mua', width: '240px' },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '140px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => {
        const statusItem = statusOptions.find((s) => s.id === row.status);
        const color = statusItem?.color || row.status_color || '';
        const label = statusItem?.name || row.status_display || row.status_code || row.status;
        return <Typography sx={{ color: color || 'inherit', fontWeight: 600, fontSize: '0.875rem' }}>{label}</Typography>;
      },
    },

    { id: 'supplier', label: 'Nhà cung cấp', width: '100%' },
    { id: 'amount', label: 'Giá trị', width: '160px', align: 'right', render: (_: unknown, row: InventoryPurchaseOrderRow) => row.amount.toLocaleString('vi-VN') },
    {
      id: 'date',
      label: 'Ngày',
      width: '140px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => {
        if (!row.date) return '';
        const parsed = parseISO(row.date);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : row.date;
      },
    },
    {
      id: 'created_by_name',
      label: 'Người tạo',
      width: '180px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => (
        <EmployeeDisplay
          name={row.created_by_name || undefined}
          employeeId={row.created_by || undefined}
          fallbackText='-'
        />
      ),
    },
    {
      id: 'created_at',
      label: 'Ngày tạo',
      width: '150px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => {
        if (!row.date) return '-';
        const parsed = parseISO(row.date);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : row.date;
      },
    },
    {
      id: 'updated_by_name',
      label: 'Người cập nhật',
      width: '180px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => (
        <EmployeeDisplay
          name={row.updated_by_name || undefined}
          employeeId={row.updated_by || undefined}
          fallbackText='-'
        />
      ),
    },
    {
      id: 'updated_at',
      label: 'Ngày cập nhật',
      width: '150px',
      render: (_: unknown, row: InventoryPurchaseOrderRow) => {
        if (!row.updated_at) return '-';
        const parsed = parseISO(row.updated_at);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy HH:mm') : row.updated_at;
      },
    },
  ];

  const totalCount = pagination?.count || rows.length;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || rows.length || DEFAULT_PAGE_SIZE;
  const totalPages = pagination?.totalPages || 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={handleToggleRow}
        onSelectAll={handleToggleSelectAll}
        onRowClick={(row) => onSelectRow(row as InventoryPurchaseOrderRow)}
        getRowId={(row) => (row as InventoryPurchaseOrderRow).id}
        isRowSelected={(row) => selectedRowIds.includes((row as InventoryPurchaseOrderRow).id)}
        areAllRowsSelected={rows.length > 0 && selectedRowIds.length === rows.length}
        areSomeRowsSelected={selectedRowIds.length > 0 && selectedRowIds.length < rows.length}
        showCheckbox={canBulkSelect}
      />
      {selectedRowIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            px: 2,
            py: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
              Đã chọn {selectedRowIds.length} phiếu
            </Typography>
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <Select value={bulkAction} displayEmpty onChange={handleBulkActionChange}>
                <MenuItem value='' disabled>
                  Chọn thao tác
                </MenuItem>
                {canUpdate ? <MenuItem value='approve'>Duyệt</MenuItem> : null}
                {canDelete ? <MenuItem value='delete'>Xóa</MenuItem> : null}
              </Select>
            </FormControl>
            <Button variant='contained' size='small' onClick={handleApplyBulkAction} disabled={!bulkAction || bulkActionLoading}>
              Apply
            </Button>
          </Box>
        </Box>
      )}
      <AppPagination
        count={totalCount}
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='phiếu mua'
        onPageChange={onPageChange}
      />
    </Box>
    
  );
}